import { Modal, ModalHandle } from 'Mri.Web.Common/Components/Modal';
import styles from './MonitoringLogModal.module.scss';
import React, { useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { Button, Form, FormFeedback, FormGroup, Input, Label } from 'reactstrap';
import { ActionType, CustomerServiceDashboardContext, createMonitoringLogEvent } from '../../context';
import { EventType, MonitoringLog } from '../../../../Controllers/CustomerServiceDashboard/CustomerServiceDashboard';
import { useStore } from '../../../hooks/use-store';
import { set } from 'date-fns';
import classNames from 'classnames/bind';
const classes = classNames.bind(styles);

export interface MonitoringLogModalProps {
    readonly modalHandle: ModalHandle;
}

export const MonitoringLogModal = (props: MonitoringLogModalProps) => {
    const { modalHandle } = props;
    const { state, dispatch } = useContext(CustomerServiceDashboardContext);
    const { selectedLog, selectedDisplay } = state;
    const { accountStore } = useStore();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<any>(null);

    type MonitoringLogFormValue<T> = {
        value: T;
        validator?(value: T, required?: boolean): boolean;
        errorMessage?: string;
        required?: boolean;
    };

    interface MonitoringLogForm {
        incidentType: MonitoringLogFormValue<number>;
        description: MonitoringLogFormValue<string>;
        resolution: MonitoringLogFormValue<string>;
        resolved: MonitoringLogFormValue<boolean>;
        ticket: MonitoringLogFormValue<string>;
    }

    const baseMonitoringLog: MonitoringLogForm = {
        incidentType: {
            value: 0,
            required: true,
        },
        description: {
            value: '',
            validator: value => value.length < 2001,
            errorMessage: 'Description must be 2000 characters or less.',
        },
        resolution: {
            value: '',
            validator: value => value.length < 2001,
            errorMessage: 'Resolution must be 2000 characters or less.',
        },
        resolved: {
            value: false,
        },
        ticket: {
            value: '',
            validator: (value, required = false) => !!extractTicketNumberFromUrl(value) || !required,
            errorMessage: 'Please input the ticket number or ticket Url.',
        },
    };
    const validateField = (field: MonitoringLogFormValue<any>) => {
        if (field.validator) {
            return field.validator(field.value);
        }
        return true;
    };
    interface FormReducerAction {
        type: string;
        payload: any;
    }
    const formReducer = (currentFormState: MonitoringLogForm, action: FormReducerAction) => {
        switch (action.type) {
            case 'incidentType':
                return {
                    ...currentFormState,
                    incidentType: { ...currentFormState.incidentType, value: action.payload },
                };
            case 'description':
                return { ...currentFormState, description: { ...currentFormState.description, value: action.payload } };
            case 'resolution':
                return { ...currentFormState, resolution: { ...currentFormState.resolution, value: action.payload } };
            case 'ticket': {
                return { ...currentFormState, ticket: { ...currentFormState.ticket, value: action.payload } };
            }
            case 'resolved':
                return { ...currentFormState, resolved: { ...currentFormState.resolved, value: action.payload } };
            case 'reset':
                return baseMonitoringLog;
            default:
                return currentFormState;
        }
    };
    const isFormValidReducer = (currentFormState: MonitoringLogForm): boolean => {
        let isValid = true;
        Object.keys(currentFormState).forEach(key => {
            const field = currentFormState[key as keyof MonitoringLogForm] as MonitoringLogFormValue<any>;
            if (!validateField(field) && (field.required || field.value !== '')) {
                isValid = false;
            }
        });
        return isValid;
    };

    /**
     * Takes a support url and pulls the ticket number from it
     * @param url - Support ticket url
     */
    const extractTicketNumberFromUrl = (urlOrTicketNumber: string): number | undefined => {
        const regex = /\/tickets\/(\d+)/;
        const match = urlOrTicketNumber.match(regex);
        const result = ['', ''];
        if (match) {
            return parseInt(match[1]);
        } else {
            const ticketNumber = parseInt(urlOrTicketNumber);
            return isNaN(ticketNumber) ? undefined : ticketNumber;
        }
    };

    const submitMonitoringLog = () => {
        const isFormValid = isFormValidReducer(formState);
        const createMonitoringLogEventAbortController = new AbortController();
        const { signal } = createMonitoringLogEventAbortController;
        const monitoringLog = {
            accountNumber: Number(selectedDisplay.account),
            displaySerial: selectedDisplay.serialNumber,
            userName: accountStore.user?.userName ?? '',
        } as MonitoringLog;
        if (!!formState.incidentType.value) monitoringLog.incidentType = formState.incidentType.value;
        if (!!formState.description.value) monitoringLog.description = formState.description.value;
        if (!!formState.resolution.value) monitoringLog.resolution = formState.resolution.value;
        if (!!formState.resolved.value) monitoringLog.resolved = formState.resolved.value;
        if (!!extractTicketNumberFromUrl(formState.ticket.value))
            monitoringLog.ticketId = extractTicketNumberFromUrl(formState.ticket.value);

        setLoading(true);
        setError(null);

        createMonitoringLogEvent(monitoringLog, { signal })
            .then(response => {
                if (dispatch) dispatch({ type: ActionType.CREATE_MONITORING_LOG_EVENT_SUCCESS, payload: response });
                setLoading(false);
            })
            .catch(err => {
                setLoading(false);
                setError(err.message);
                console.error('MonitoringLogModal onSubmit error', err.message);
                if (dispatch) dispatch({ type: ActionType.CREATE_MONITORING_LOG_EVENT_FAILURE, payload: err.message });
            })
            .finally(() => {
                if (!error) closeModal();
            });
    };

    const resetForm = () => {
        formDispatch({ type: 'reset', payload: null });
    };
    const closeModal = () => {
        resetForm();
        modalHandle.close();
    };

    const [formState, formDispatch] = useReducer(formReducer, baseMonitoringLog);
    const formValid = useMemo(() => isFormValidReducer(formState), [formState]);

    const Footer = useMemo(() => {
        return (
            <>
                <Button onClick={closeModal}>Cancel</Button>
                <Button color="primary" onClick={() => submitMonitoringLog()} disabled={!formValid}>
                    Submit
                </Button>
            </>
        );
    }, [formValid]);

    return (
        <>
            <Modal modalHandle={modalHandle} footer={Footer}>
                {selectedDisplay && (
                    <div>
                        <h3>New Log Event for Display {selectedDisplay.serialNumber}</h3>
                        <Form>
                            <div className={classes('form-body')}>
                                <FormGroup>
                                    <Label for="incidentType">Incident Type</Label>
                                    <Input
                                        id="incidentType"
                                        type="select"
                                        value={formState.incidentType.value}
                                        valid={validateField(formState.incidentType)}
                                        onChange={e => formDispatch({ type: 'incidentType', payload: e.target.value })}
                                    >
                                        {Object.keys(EventType)
                                            .filter(key => isNaN(Number(key)))
                                            .map(key => (
                                                <option key={key} value={EventType[key as keyof typeof EventType]}>
                                                    {key}
                                                </option>
                                            ))}
                                    </Input>
                                </FormGroup>
                                {formState.incidentType.value == EventType.Ticket && (
                                    <FormGroup>
                                        <Label for="ticket">Ticket</Label>
                                        <Input
                                            id="ticket"
                                            type="text"
                                            required={formState.incidentType.value == EventType.Ticket}
                                            value={formState.ticket.value}
                                            valid={validateField(formState.ticket)}
                                            placeholder="Ticket Number or Ticket Url"
                                            onChange={e => formDispatch({ type: 'ticket', payload: e.target.value })}
                                        />
                                        <FormFeedback>{formState.ticket.errorMessage}</FormFeedback>
                                    </FormGroup>
                                )}
                                <FormGroup>
                                    <Label for="description">Description of the issue</Label>
                                    <Input
                                        id="description"
                                        type="textarea"
                                        value={formState.description.value}
                                        valid={validateField(formState.description)}
                                        onChange={e => formDispatch({ type: 'description', payload: e.target.value })}
                                    />
                                    <FormFeedback>{formState.description.errorMessage}</FormFeedback>
                                </FormGroup>
                                <FormGroup className={classes('checkbox')}>
                                    <Label for="resolved">Resolved</Label>
                                    <Input
                                        id="resolved"
                                        type="checkbox"
                                        checked={formState.resolved.value}
                                        onChange={e => formDispatch({ type: 'resolved', payload: e.target.checked })}
                                    />
                                </FormGroup>
                                {formState.resolved.value && (
                                    <FormGroup>
                                        <Label for="resolution">Resolution</Label>
                                        <Input
                                            id="resolution"
                                            type="textarea"
                                            value={formState.resolution.value}
                                            valid={validateField(formState.resolution)}
                                            onChange={e =>
                                                formDispatch({ type: 'resolution', payload: e.target.value })
                                            }
                                        />
                                        <FormFeedback>{formState.resolution.errorMessage}</FormFeedback>
                                    </FormGroup>
                                )}
                            </div>
                        </Form>
                        {error && <div className="alert alert-danger">{error.message}</div>}
                    </div>
                )}
            </Modal>
        </>
    );
};
